import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GeoFolder } from 'cartoservice/static/cartoservice/src/classes';
import { of } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { LayersAutocompleteComponent } from '../';
import { Attribute, Layer, Project, Service, Source } from '../../classes';
import {AppService, LayerService, ServicesService} from '../../services';
import {Utils as SharedUtils} from 'shared/classes';

@Component({
  selector: 'geo-layers',
  templateUrl: 'layers.component.html',
  styleUrls: ['./layers.component.less']
})
export class LayersComponent implements OnInit {
  @ViewChild('select') select: LayersAutocompleteComponent;

  @Input() project: Project;

  sources: Source[] = [];
  folders: GeoFolder[] = [];
  services: Service[] = [];
  currentLayer: Layer;
  currentGroup: Layer;
  form: FormGroup;
  editMode = false;
  attributesLoading = false;
  loading = false;
  colorWindow = {show: false, top: 0, left: 0};


  constructor(
    private appService: AppService,
    private layerService: LayerService,
    private ref: ChangeDetectorRef,
    private builder: FormBuilder,
    private servicesService: ServicesService
  ) {}

  ngOnInit() {
    this.getServerList();

    this.form = this.builder.group({
      source: [null, [Validators.required]],
      folder: [null],
      service: [null, [Validators.required]],
      name: [null, [Validators.required, Validators.maxLength(255)]],
      visible: [false],
      editable: [true],
      rating: [false],
      searchable: [true],
      inTooltipDefault: [true],
      image: [true],
      chart: [false]
    });

    // в режиме редактирования на каждое изменение значения в форме
    // вносим изменения в объект слоя
    this.form.valueChanges.pipe(filter(() => this.editMode)).subscribe(value => {
      const formModel = Object.assign({}, value);
      Object.assign(this.currentLayer, {
        name: formModel.name,
        visible: formModel.visible,
        editable: formModel.editable,
        rating: formModel.rating,
        searchable: formModel.searchable,
        inTooltipDefault: formModel.inTooltipDefault,
        popup_chart: formModel.chart,
        popup_image: formModel.image
      });
    });

    // ресет формы при смене источника
    this.form
      .get('source')
      .valueChanges.pipe(
        tap(() => {
          this.form.patchValue({
            folder: null,
            service: null,
            name: null,
            visible: false,
            editable: false,
            rating: false,
            searchable: true,
            inTooltipDefault: true,
            image: true,
            chart: false
          });
          this.loading = true;
        }),
        switchMap(value => {
          if (value.default) {
            if (this.folders.length) {
              this.loading = false;
              this.form.patchValue({ folder: this.folders[0] });
              return of(this.folders);
            }
            return this.servicesService.getFolders().pipe(
              tap(folders => {
                this.folders = folders.filter(folder => folder.type !== 'default');
                this.folders.sort((a, b) => a.title.localeCompare(b.title));
                const allServices = folders.reduce((acc, folder) => [...acc, ...folder.services], []);
                allServices.sort((a, b) => a.title.localeCompare(b.title));
                this.folders.unshift(
                  new GeoFolder({
                    title: 'Все',
                    services: allServices
                  })
                );
                this.form.patchValue({ folder: this.folders[0] });
              }),
              finalize(() => (this.loading = false))
            );
          }
          return this.servicesService.getServices(value).pipe(
            tap(services => {
              this.services = services;
            }),
            finalize(() => (this.loading = false))
          );
        })
      )
      .subscribe();

    this.form
      .get('folder')
      .valueChanges.pipe(filter(value => !!value))
      .subscribe(value => {
        // ресет формы при смене папки
        this.form.patchValue({
          service: null,
          name: null,
          visible: false,
          editable: false,
          rating: false,
          searchable: true,
          inTooltipDefault: true,
          image: true,
          chart: false
        });
        this.services = value.services;
      });

    // по умолчанию даем слою то же название, что и у выбранного сервиса
    this.form
      .get('service')
      .valueChanges.pipe(filter(value => {
        return !this.editMode && !!value;
      }))
      .subscribe(value => {
        this.form.patchValue({ name: value.title });
      });
  }

  get showStyleLink() {
    let ret = false;
    const layer = this.currentLayer as any;
    if (layer && layer.content_object) {
      ret = true;
    }
    return ret;
  }

  isItemInclude(layer: Layer): boolean {
    return true;
  }

  getVisibleStyle(isVisible: boolean): string {
    return isVisible ? 'visible' : 'hidden';
  }

  onLayerClick(layer: Layer) {
    const newLayer = layer === this.currentLayer ? null : layer;
    this.currentLayer = newLayer;
    if (newLayer) {
      this.editLayer(this.currentLayer);
    } else {
      this.resetForm();
    }
  }

  onTreeButtonClick(evt: any) {
    const layer = evt.layer as Layer;
    if (evt.action === 'color') {
      this.currentGroup = layer;
      if (!this.currentGroup.backColor) {
        this.currentGroup.backColor = '#f4f4f4';
      }
      this.colorWindow.show = true;
      const pos = SharedUtils.getRelativePosition(evt.target, '.edit-forms');
      this.colorWindow.left = pos.left;
      this.colorWindow.top = pos.top;
    } else if (evt.action === 'deleteGroup') {
      const message = 'Хотите удалить группу ' + layer.name + '?';
      this.appService.openAlertWindow(message, ['Удалить', 'Отмена'], 0).subscribe( (buttonIndex: number) => {
        if (buttonIndex === 0) {
          this.deleteLayer(layer);
        }
      });
    }
  }

  onColorWindowClose() {
    this.colorWindow.show = false;
  }

  onChangeColor(color: string) {
    this.currentGroup.backColor = color;
  }

  onDeleteLayerClick() {
    const message = 'Хотите удалить слой ' + this.currentLayer.name + '?';
      this.appService.openAlertWindow(message, ['Удалить', 'Отмена'], 0).subscribe( (buttonIndex: number) => {
      if (buttonIndex === 0) {
        this.deleteLayer(this.currentLayer);
      }
    });
  }

  onLayerMove(evt: any) {
    const targetArray = evt.targetArray as Layer[];
    let targetIndex = evt.targetIndex as number;
    const movedLayer = evt.movedLayer as Layer;
    // Удаление слоя
    let deleteArrayFrom: Layer[] = null;
    let deleteIndex = -1;
    this.layerService.walkLayersRecursive(this.project.layers, (layer: Layer, layers: Layer[]) => {
      if (layer === movedLayer) {
        const index = layers.indexOf(layer);
        deleteArrayFrom = layers;
        deleteIndex = index;
        layers.splice(index, 1);
        return false;
      } else {
        return true;
      }
    });
    // Вставка слоя
    if (targetIndex < 0) {
      targetArray.unshift(movedLayer);
    } else {
      // Коррекция индекса вставки
      if (deleteArrayFrom === targetArray && deleteIndex < targetIndex) {
        targetIndex--;
      }
      targetArray.splice(targetIndex + 1, 0, movedLayer);
    }
  }

  onOrderChanged() {

  }

  addGroup() {
    const group = new Layer();
    group.isGroup = group.is_group = true;
    group.name = group.name = 'Новая группа';
    group.subLayers = group.sublayers = [];
    group.id = Math.floor(Math.random() * 1000000);
    this.project.layers.unshift(group);
  }

  addLayer() {
    const formModel = Object.assign({}, this.form.value);

    const layer = new Layer({
      service: formModel.service,
      name: formModel.name,
      title: formModel.name,
      isGroup: false,
      is_group: false,
      visible: formModel.visible,
      editable: formModel.editable,
      rating: formModel.rating,
      searchable: formModel.searchable,
      inTooltipDefault: formModel.inTooltipDefault,
      popup_chart: formModel.chart,
      popup_image: formModel.image
    });

    layer.id = Math.floor(Math.random() * 1000000);
    this.project.layers.unshift(layer);
    this.resetForm();
  }

  deleteLayer(layer: Layer) {
    if (layer.resource_uri) {
      this.project.deletedLayers.push(layer.resource_uri);
    }

    let idx: number;

    if (layer.parent) {
      idx = layer.parent.subLayers.indexOf(layer);
      layer.parent.subLayers.splice(idx, 1);
    } else {
      idx = this.project.layers.indexOf(layer);
      this.project.layers.splice(idx, 1);
    }

    this.currentLayer = null;
    this.resetForm();
  }

  editLayer(layer: Layer) {
    if (layer.is_group || this.loading) {
      return;
    }

    this.currentLayer = layer;
    this.editMode = true;

    this.form.patchValue({
      service: layer.service,
      name: layer.name,
      visible: layer.visible,
      editable: layer.editable,
      rating: layer.rating,
      searchable: layer.searchable,
      inTooltipDefault: layer.inTooltipDefault,
      image: layer.popup_image,
      chart: layer.popup_chart
    });

    if (!layer.attributes) {
      if (layer.service.type === 'geoserver') {
        layer.attributes = (layer.service as Service).attributes;
      } else {
        this.attributesLoading = true;
        this.layerService
          .getAttributes(layer)
          .pipe(finalize(() => (this.attributesLoading = false)))
          .subscribe(attributes => {
            layer.attributes = attributes;
            if (layer.crowdsource) {
              layer.crowdsourceAttributes = attributes.filter(attribute => attribute.crowdSource);
            }
          });
      }
    }
  }

  onAttrInPopupChange(attr: Attribute) {
    if (!attr.inPopup) {
      attr.aliasInPopup = false;
    }
  }

  resetForm() {
    this.editMode = false;
    this.form.reset({
      source: this.sources[0],
      folder: this.folders[0],
      visible: false,
      editable: false,
      rating: false,
      searchable: true,
      inTooltipDefault: true,
      image: true,
      chart: false
    });
  }

  getLayerStyleUrl(layer: Layer) {
    return layer.content_object.replace('api/v1/', '');
    // return `/cartoservice/wms/${layer.object_id}`;
  }

  private getServerList() {
    this.servicesService.getServers().subscribe((sources: Source[]) => {
      // добавляем дефолтный источник (сервисы Геоаналитики)
      const geoanalitikaSource = new Source();
      geoanalitikaSource.title = 'Геоаналитика';
      geoanalitikaSource.default = true;
      sources.unshift(geoanalitikaSource);
      this.sources = sources;
      this.form.patchValue({ source: sources[0] });
    });
  }

  onMouseDown(evt: MouseEvent) {
    evt.stopPropagation();
  }
}
